import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
  Field,
  FieldValue,
  FieldValues,
  SearchResults,
  UserFriendlyError,
} from 'models';
import { NGXLogger } from 'ngx-logger';
import { deriveLoading } from 'ngxtension/derive-loading';
import { finalize } from 'rxjs';
import { UPDATE_DOCUMENTS_PROVIDER } from 'src/app/common/tokens';
import { IndexFieldHelperService } from 'src/app/services/index-field-helper.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';

export interface UpdateMultipleSearchResultsDialogData {
  databaseId: number;
  archiveId: number;
  searchResults: SearchResults;
}

/** Describes the reference type for the merge document success dialog. */
export type UpdateMultipleSearchResultsDialogRef = MatDialogRef<
  UpdateMultipleSearchResultsDialogComponent,
  boolean
>;

@Component({
  selector: 'app-update-multiple-search-results-dialog',
  templateUrl: './update-multiple-search-results-dialog.component.html',
  styleUrls: ['./update-multiple-search-results-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UpdateMultipleSearchResultsDialogComponent {
  private archivesQuery = inject(ArchivesQuery);
  private dialogData: UpdateMultipleSearchResultsDialogData =
    inject(MAT_DIALOG_DATA);
  private dialogRef: UpdateMultipleSearchResultsDialogRef =
    inject(MatDialogRef);
  private formBuilder = inject(FormBuilder);
  private indexFieldHelper = inject(IndexFieldHelperService);
  private logger = inject(NGXLogger);
  private notify = inject(NotificationService);
  private updateDocuments = inject(UPDATE_DOCUMENTS_PROVIDER);

  private archiveTableFields = this.archivesQuery.getTableFields(
    this.dialogData.archiveId
  );
  private saveInProgress = signal(false);

  /** All archive fields. */
  archiveFields = this.archivesQuery
    .getFields(this.dialogData.archiveId)
    .filter(
      (field) =>
        !this.archiveTableFields.some((tableField) =>
          tableField.fieldIds.includes(field.id)
        ) &&
        !field.systemField &&
        field.liveField === undefined // Live fields can't be editted or shown here with multiple documents.
    );
  indexerForm: UntypedFormGroup;
  /** Determines if the save progress bar should be shown. */
  showSaveProgressBar = signal(false);

  /**
   * Whether the save button should be disabled.
   *
   * @returns True if the save button should be disabled.
   */
  get disableSave(): boolean {
    // This could be a signal but form state doesn't seem to like being put into a signal so a getter is necessary here.
    return (
      this.saveInProgress() ||
      !this.indexerForm?.dirty ||
      this.indexerForm?.invalid
    );
  }

  constructor() {
    const controls: {
      // biome-ignore lint/suspicious/noExplicitAny: Any must be used here because our field controls currently cannot be typed.
      [key: string]: AbstractControl<any, any>;
    } = {};

    for (const field of this.archiveFields) {
      if (field.multiValue) {
        const mvControls = [new UntypedFormControl()];
        controls[field.id.toString()] = this.formBuilder.array(mvControls);
      } else {
        const control = new UntypedFormControl(
          '',
          Validators.maxLength(field.length)
        );

        // return { [field.id]: control };
        controls[field.id.toString()] = control;
      }

      // add control for clear
      controls[`clear_${field.id}`] = new FormControl(false);
    }

    this.indexerForm = this.formBuilder.group(controls);
  }

  static open(
    dialogService: MatDialog,
    data: UpdateMultipleSearchResultsDialogData
  ): UpdateMultipleSearchResultsDialogRef {
    return dialogService.open<
      UpdateMultipleSearchResultsDialogComponent,
      UpdateMultipleSearchResultsDialogData,
      boolean
    >(UpdateMultipleSearchResultsDialogComponent, {
      data,
      width: '70vw',
    });
  }

  /**
   * Get a form control.
   *
   * @param fieldId Field Id.
   *
   * @returns A form control.
   */
  getFormControl(fieldId: number): UntypedFormControl {
    return this.indexerForm.get(fieldId.toString()) as UntypedFormControl;
  }

  /**
   * Gets the backing form array for a multivalue field.
   *
   * @param fieldId Field Id.
   * @returns A form array.
   */
  getMultiValueFormArray(fieldId: number): UntypedFormArray {
    return this.indexerForm.get(fieldId.toString()) as UntypedFormArray;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  /**
   * Handles the toggle change event on a field.
   *
   * @param event Slide toggle change event.
   * @param field Field.
   */
  onClearToggleChange(event: MatSlideToggleChange, field: Field) {
    const valueControl = field.multiValue
      ? this.getMultiValueFormArray(field.id)
      : this.getFormControl(field.id);
    const clearControl = this.indexerForm.get(
      'clear_' + field.id
    ) as FormControl<boolean>;
    clearControl.setValue(event.checked);
    if (event.checked) {
      if (field.multiValue) {
        const mvValueControl = valueControl as UntypedFormArray;
        mvValueControl.clear();
        mvValueControl.push(new UntypedFormControl(''));
      } else {
        const valueControl = this.getFormControl(field.id);
        valueControl.setValue('');
      }
      valueControl.markAsDirty();
      valueControl.disable({ emitEvent: false, onlySelf: true });
    } else {
      valueControl.enable();
    }
  }

  /** Handler for the save click event. */
  onSave(): void {
    this.saveInProgress.set(true);
    const fieldValues = this.getFieldValues();
    for (let index = fieldValues.length - 1; index >= 0; index--) {
      const fieldValue = fieldValues[index];
      const fieldContainsValue =
        !!fieldValue.value ||
        (fieldValue.multiValue.length > 0 &&
          fieldValue.multiValue.some((mv) => mv));
      const clearControl = this.indexerForm.get(
        'clear_' + fieldValue.id
      ) as FormControl<boolean>;

      if (!clearControl.value && !fieldContainsValue) {
        // Clear toggle is not set for this field so remove it from the fields to update.
        fieldValues.splice(index, 1);
      }
    }

    if (fieldValues.length === 0) {
      // The user did something to trigger the form to be dirty but the finally resulting data does not contain anything to update.
      this.logger.warn(
        'Save was clicked but there is actually no new data to update. The update operation will not be sent to the API.'
      );
      this.dialogRef.close(false); // Close the dialog as cancelled because there was nothing to do.
      return;
    }
    const { databaseId, archiveId, searchResults } = this.dialogData;
    const documents: Record<number, string> = searchResults.reduce(
      (acc, result) => {
        acc[result.id] = result.secureId;
        return acc;
      },
      {} as Record<number, string>
    );
    const updateDocuments$ = this.updateDocuments
      .updateFieldData(databaseId, archiveId, documents, fieldValues)
      .pipe(
        finalize(() => {
          this.saveInProgress.set(false);
        })
      );

    // Create and subscribe to an observable of the update to show a progress bar if the operation takes a while.
    updateDocuments$.pipe(deriveLoading()).subscribe((show) => {
      this.showSaveProgressBar.set(show);
    });

    updateDocuments$.subscribe({
      next: (result) => {
        const errorCount = Object.keys(result.errors).length;
        if (errorCount === 0) {
          this.notify.success('UPDATE_DOCUMENTS_SUCCESS');
          this.dialogRef.close(true); // Close the dialog because there is nothing else to do.
          return;
        }

        // If we got here at least one document errored. Do not close the dialog in case there is something the user can correct to fix the errors.
        for (const documentIdString in result.errors) {
          // Log each error to the console.
          const documentId = Number(documentIdString);
          this.logger.error(
            `Failed to update document ${documentId}.`,
            result.errors[documentIdString]
          );
        }

        // Check if all documents errored.
        const allDocumentsErrored = errorCount === searchResults.length;

        if (allDocumentsErrored) {
          // Notify as an error if all documents errored.
          this.notify.error(
            new UserFriendlyError(
              undefined,
              'All documents failed to update',
              'ERROR_UPDATE_DOCUMENTS_ALL_FAILED'
            )
          );
        } else {
          // Warn the user that one or more but not all of the documents failed to update.
          this.notify.warning('ERROR_UPDATE_DOCUMENTS_SOME_FAILED');
        }
      },
      error: (error: UserFriendlyError) => {
        this.logger.error('Failed to update documents', error);
        error.i18n = 'ERROR_UPDATE_DOCUMENTS_ALL_FAILED';
        this.notify.error(error);
      },
    });
  }

  private getFieldValues(): FieldValues {
    const fieldValues: FieldValues = [];
    for (const field of this.archiveFields) {
      if (
        field.systemField ||
        !this.indexerForm.get(field.id.toString())?.dirty
      ) {
        // Ignore any system field.
        // Ignore any field that is not dirty so we are only telling the API to update the data we actually want to change.
        continue;
      }

      const fieldValue: FieldValue = {
        id: field.id,
        multiValue: [],
        value: '',
      };

      if (field.multiValue) {
        const formArray = this.indexerForm.get(
          field.id.toString()
        ) as UntypedFormArray;

        // Remove all empty MV field form controls.
        for (let index = formArray.length - 1; index >= 0; index--) {
          const control = formArray.at(index);
          if (!control.value && index !== 0) {
            formArray.removeAt(index);
          } else if (!control.value && index === 0) {
            control.setValue('');
          }
        }

        // get values from all the mv form controls.
        for (const [, control] of Object.entries(formArray.controls)) {
          const value = this.indexFieldHelper.getFormValue(control, field);
          fieldValue.multiValue.push(value);
        }
      } else {
        const control = this.indexerForm.get(
          field.id.toString()
        ) as UntypedFormControl;
        if (!control) {
          continue;
        }

        const value = this.indexFieldHelper.getFormValue(control, field);
        fieldValue.value = value;
      }

      fieldValues.push(fieldValue);
    }

    return fieldValues;
  }
}
